import React, { useState, useEffect, useContext } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Box,
} from "@material-ui/core";
import { Search, FilterList as FilterListIcon } from "@material-ui/icons";
import { getStandardButtonStyles } from "../../styles/standardButtons";
import MainContainer from "../../components/MainContainer";
import PageHelpGuide from "../../components/PageHelpGuide";
import { BOT_MONITOR_HELP_DATA } from "../../constants/pageHelp/botMonitorHelp";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  cyanButton: {
    backgroundColor: '#00b0ff',
    color: '#fff',
    borderRadius: '4px',
    textTransform: 'none',
    fontWeight: 'bold',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#0091ea',
      boxShadow: '0 4px 12px rgba(0, 176, 255, 0.4)',
      transform: 'translateY(-2px)'
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: 'none'
    }
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.type === "dark" ? "#2A2D32 !important" : theme.palette.background.paper,
    borderRadius: 16,
  },
  filterContainer: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    paddingLeft: 20,
    paddingRight: 20,
  },
  headerWrapper: {
    display: "flex",
    alignItems: "flex-start",
    marginTop: 28,
    marginBottom: 30,
    paddingLeft: 20,
    paddingRight: 20,
    flexShrink: 0,
  },
  pageTitle: {
    fontWeight: 700,
    letterSpacing: "-0.5px",
    color: theme.palette.type === "dark" ? "#FFF" : theme.palette.primary.main,
  },
  card: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "12px",
    backgroundColor: theme.palette.type === "dark" ? "#2A2D32" : "#FFFFFF",
    color: theme.palette.type === "dark" ? "#fff" : "#000",
    border: `1px solid ${theme.palette.primary.main}`,
    boxShadow: theme.palette.type === "dark"
      ? `0 0 12px ${theme.palette.primary.main}80`
      : `0 0 10px ${theme.palette.primary.main}40`,
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.palette.type === "dark"
        ? `0 4px 15px ${theme.palette.primary.main}`
        : `0 4px 15px ${theme.palette.primary.main}60`,
    },
  },
  cardTitle: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  cardValue: {
    fontSize: "2.5rem",
    fontWeight: "bold",
  },
  tableContainer: {
    flex: 1,
    overflowY: "auto",
    maxHeight: "calc(100vh - 380px)",
    ...theme.scrollbarStyles,
  },
  tableWrapper: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    padding: theme.spacing(1),
    marginTop: theme.spacing(2),
    borderRadius: "10px",
    overflow: "hidden",
  },
  tableHeaderCell: {
    fontWeight: "bold",
    backgroundColor: "#1e1e1e",
    color: "#fff",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    position: "sticky",
    top: 0,
    padding: "10px 16px",
  },
  sortLabel: {
    color: '#fff',
    '&:hover': {
      color: '#e0e0e0',
    },
    '&.MuiTableSortLabel-active': {
      color: '#fff',
    },
    '& .MuiTableSortLabel-icon': {
      color: '#fff !important',
    },
  },
}));

const BotMonitor = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [dateStart, setDateStart] = useState(moment().format("YYYY-MM-DD"));
  const [dateEnd, setDateEnd] = useState(moment().format("YYYY-MM-DD"));
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'totalNew', direction: 'desc' });

  const isSuper = user.super && user.profile === "admin";

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await api.get("/bot-monitor", {
        params: {
          dateStart,
          dateEnd,
        },
      });
      setData(response.data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalNew = data.reduce((acc, curr) => acc + curr.totalNew, 0);
  const totalResponded = data.reduce((acc, curr) => acc + curr.totalResponded, 0);
  const totalNotResponded = totalNew - totalResponded;
  const responseRate = totalNew > 0 ? ((totalResponded / totalNew) * 100).toFixed(2) : 0;

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    let sortableItems = [...data];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key] || "";
        let bValue = b[sortConfig.key] || "";

        if (sortConfig.key === 'notResponded') {
          aValue = a.totalNew - a.totalResponded;
          bValue = b.totalNew - b.totalResponded;
        } else if (sortConfig.key === 'responseRate') {
          aValue = a.totalNew > 0 ? (a.totalResponded / a.totalNew) : 0;
          bValue = b.totalNew > 0 ? (b.totalResponded / b.totalNew) : 0;
        } else if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = typeof bValue === 'string' ? bValue.toLowerCase() : bValue;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [data, sortConfig]);

  return (
    <MainContainer>
      <Paper className={classes.mainPaper} variant="outlined">
        <Box className={classes.headerWrapper}>
          <Box display="flex" alignItems="center" style={{ gap: '14px' }}>
            <PageHelpGuide
              helpData={BOT_MONITOR_HELP_DATA}
              tooltipText={i18n.t("botMonitor.helpTooltip", "Guía y Ayuda de Monitoreo de Bots (Haz clic para ver)")}
            />
            <Typography variant="h5" className={classes.pageTitle}>
              {i18n.t("botMonitor.title")}
            </Typography>
          </Box>
        </Box>
        <div className={classes.filterContainer}>
          <TextField
            label={i18n.t("botMonitor.startDate")}
            type="date"
            value={dateStart}
            onChange={(e) => setDateStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            size="small"
          />
          <TextField
            label={i18n.t("botMonitor.endDate")}
            type="date"
            value={dateEnd}
            onChange={(e) => setDateEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            size="small"
          />
          <Button
            size="small"
            variant="outlined"
            className={classes.applyButton}
            onClick={loadData}
            startIcon={<FilterListIcon style={{ fontSize: 18 }} />}
            disabled={loading}
          >
            {i18n.t("botMonitor.filter")}
          </Button>
        </div>

        <Grid container spacing={3} style={{ marginBottom: "24px" }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.card} variant="outlined">
              <Typography className={classes.cardTitle}>{i18n.t("botMonitor.incoming")}</Typography>
              <Typography className={classes.cardValue} style={{ color: "#2196f3" }}>
                {totalNew}
              </Typography>
              <Typography variant="body2">{i18n.t("botMonitor.newConvos")}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.card} variant="outlined">
              <Typography className={classes.cardTitle}>{i18n.t("botMonitor.responded")}</Typography>
              <Typography className={classes.cardValue} style={{ color: "#4caf50" }}>
                {totalResponded}
              </Typography>
              <Typography variant="body2">{i18n.t("botMonitor.respondedSuccess")}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.card} variant="outlined">
              <Typography className={classes.cardTitle}>{i18n.t("botMonitor.notResponded")}</Typography>
              <Typography className={classes.cardValue} style={{ color: "#f44336" }}>
                {totalNotResponded}
              </Typography>
              <Typography variant="body2">{i18n.t("botMonitor.waitFail")}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.card} variant="outlined">
              <Typography className={classes.cardTitle}>{i18n.t("botMonitor.responseRate")}</Typography>
              <Typography className={classes.cardValue} style={{ color: "#ff9800" }}>
                {responseRate}%
              </Typography>
              <Typography variant="body2">{i18n.t("botMonitor.effectiveness")}</Typography>
            </Paper>
          </Grid>
        </Grid>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 40 }}>
            <CircularProgress />
          </div>
        ) : (
          <Paper variant="outlined" className={classes.tableWrapper}>
            <TableContainer className={classes.tableContainer}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {isSuper && (
                    <TableCell className={classes.tableHeaderCell}>
                      <TableSortLabel
                        active={sortConfig.key === 'companyName'}
                        direction={sortConfig.key === 'companyName' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('companyName')}
                        className={classes.sortLabel}
                      >
                        {i18n.t("botMonitor.companyClient")}
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell className={classes.tableHeaderCell}>
                    <TableSortLabel
                      active={sortConfig.key === 'whatsappName'}
                      direction={sortConfig.key === 'whatsappName' ? sortConfig.direction : 'asc'}
                      onClick={() => requestSort('whatsappName')}
                      className={classes.sortLabel}
                    >
                      {i18n.t("botMonitor.connection")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell}>
                    <TableSortLabel
                      active={sortConfig.key === 'campaignName'}
                      direction={sortConfig.key === 'campaignName' ? sortConfig.direction : 'asc'}
                      onClick={() => requestSort('campaignName')}
                      className={classes.sortLabel}
                    >
                      {i18n.t("botMonitor.campaignKeyword")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeaderCell}>
                    <TableSortLabel
                      active={sortConfig.key === 'totalNew'}
                      direction={sortConfig.key === 'totalNew' ? sortConfig.direction : 'asc'}
                      onClick={() => requestSort('totalNew')}
                      className={classes.sortLabel}
                    >
                      {i18n.t("botMonitor.incoming")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeaderCell}>
                    <TableSortLabel
                      active={sortConfig.key === 'totalResponded'}
                      direction={sortConfig.key === 'totalResponded' ? sortConfig.direction : 'asc'}
                      onClick={() => requestSort('totalResponded')}
                      className={classes.sortLabel}
                    >
                      {i18n.t("botMonitor.responded")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeaderCell}>
                    <TableSortLabel
                      active={sortConfig.key === 'notResponded'}
                      direction={sortConfig.key === 'notResponded' ? sortConfig.direction : 'asc'}
                      onClick={() => requestSort('notResponded')}
                      className={classes.sortLabel}
                    >
                      {i18n.t("botMonitor.notResponded")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeaderCell}>
                    <TableSortLabel
                      active={sortConfig.key === 'responseRate'}
                      direction={sortConfig.key === 'responseRate' ? sortConfig.direction : 'asc'}
                      onClick={() => requestSort('responseRate')}
                      className={classes.sortLabel}
                    >
                      {i18n.t("botMonitor.responseRate")}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map((row, index) => {
                  const notResponded = row.totalNew - row.totalResponded;
                  const rate = row.totalNew > 0 ? ((row.totalResponded / row.totalNew) * 100).toFixed(2) : 0;
                  return (
                    <TableRow key={index} hover>
                      {isSuper && <TableCell>{row.companyName}</TableCell>}
                      <TableCell>
                        <strong>{row.whatsappName}</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>{row.campaignName || i18n.t("botMonitor.noCampaign")}</strong>
                      </TableCell>
                      <TableCell align="center">{row.totalNew}</TableCell>
                      <TableCell align="center" style={{ color: "#4caf50", fontWeight: "bold" }}>
                        {row.totalResponded}
                      </TableCell>
                      <TableCell align="center" style={{ color: notResponded > 0 ? "#f44336" : "inherit", fontWeight: notResponded > 0 ? "bold" : "normal" }}>
                        {notResponded}
                      </TableCell>
                      <TableCell align="center">
                        <span style={{ 
                          padding: "4px 8px", 
                          borderRadius: "12px", 
                          backgroundColor: rate > 90 ? "#e8f5e9" : rate > 70 ? "#fff3e0" : "#ffebee",
                          color: rate > 90 ? "#2e7d32" : rate > 70 ? "#e65100" : "#c62828",
                          fontWeight: "bold"
                        }}>
                          {rate}%
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isSuper ? 7 : 6} align="center">
                      {i18n.t("botMonitor.empty")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </TableContainer>
          </Paper>
        )}
      </Paper>
    </MainContainer>
  );
};

export default BotMonitor;
